import type { ReactNode } from 'react';
import { useState } from 'react';
import type { BrandInsightsPageContext } from '@trinity/chat';
import { ChatContainer } from '@trinity/chat-components/components/BrandInsights/Chat.Container.js';
import {
  ChatViewType,
  ChatViewTypeContext,
} from '@trinity/chat-components/components/BrandInsights/context.js';
import { LinkStateContainer } from '@trinity/chat-components/components/BrandInsights/Link.Container.js';
import {
  ChatPageContextDataContext,
  DispatchPageContextDataContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { ChatStateContainer } from '@trinity/chat-components/components/Chat.State/Chat.State.js';
import { ChatPendingQueryContextContainer } from '@trinity/chat-components/components/Chat.State/ChatPendingQueryContextContainer.js';

/**
 * Reusable provider shell for the chat system. Mount above any
 * `ChatInterfaceNative` to supply chat state and page-context providers.
 *
 * Syncs the external `contextData` prop into local state so that
 * downstream consumers (e.g. context selector) can update it without
 * losing the parent-provided value when it changes.
 */
export function ChatContextProviders({
  contextData,
  children,
}: {
  contextData?: BrandInsightsPageContext.Agent;
  children: ReactNode;
}): React.JSX.Element {
  const [localContextData, setLocalContextData] = useState<BrandInsightsPageContext.Item | undefined>(
    contextData
  );
  const [prevContextData, setPrevContextData] = useState(contextData);
  if (contextData !== prevContextData) {
    setPrevContextData(contextData);
    setLocalContextData(contextData);
  }

  return (
    <ChatViewTypeContext.Provider value={ChatViewType.Page}>
      <DispatchPageContextDataContext.Provider value={setLocalContextData}>
        <ChatPageContextDataContext.Provider value={localContextData}>
          <ChatStateContainer>
            <ChatPendingQueryContextContainer>
              <LinkStateContainer>
                <ChatContainer>{children}</ChatContainer>
              </LinkStateContainer>
            </ChatPendingQueryContextContainer>
          </ChatStateContainer>
        </ChatPageContextDataContext.Provider>
      </DispatchPageContextDataContext.Provider>
    </ChatViewTypeContext.Provider>
  );
}
